import { FunctionComponent, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { desktopCSS, mobileCSS } from '@measures/responsive'
import { WebVideoProps } from '@widgets/Video/types/darkcove'
import useIsInArticleContent from '@hooks/useIsInArticleContent'
import useIsInScribbleContent from '@hooks/useIsInScribbleContent'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'
import useIsInRecipeIngredientsContent from '@hooks/useIsInRecipeIngredientsContent'
import { printCSS } from '@utils/style'

export type VideoContainerProps = Pick<WebVideoProps, 'videoId'> & {
  isInVideoOnlyArticle?: boolean
  hasPlaylist?: boolean
  isInOverlay?: boolean
  children?: ReactNode
}

interface StyledVideoContainerProps extends VideoContainerProps {
  isInArticleContent?: boolean
  isInScribble?: boolean
  isInRecipe?: boolean
  isInRecipeIngredients?: boolean
}

const StyledVideoContainer = styled.div<StyledVideoContainerProps>`
  ${({
    theme: {
      measures: {
        navigationBar: { level1HeightMobile: navigationBarLevel1HeightMobile },
        stretch,
        stretchToFitCard,
      },
    },
    isInOverlay,
    isInVideoOnlyArticle,
    hasPlaylist,
    isInArticleContent,
    isInScribble,
    isInRecipe,
    isInRecipeIngredients,
  }) => {
    // creating place for playlist on the right side of video
    const hasPlaylistCSS = css`
      ${desktopCSS(css`
        display: grid;
        grid-gap: 20px;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        grid-column: span 6;
      `)}
    `

    const inVideoOnlyArticleCSS = css`
      ${mobileCSS(css`
        position: sticky;
        top: ${navigationBarLevel1HeightMobile};
        z-index: 2;
      `)}
    `

    return css`
      position: relative;
      ${printCSS(css`
        display: none;
      `)}

      ${isInVideoOnlyArticle && !isInOverlay && inVideoOnlyArticleCSS}

      ${hasPlaylist && hasPlaylistCSS}

      ${isInArticleContent &&
      css`
        ${!isInRecipeIngredients &&
        css`
          margin-top: 16px;
          margin-bottom: 16px;
        `}

        ${!isInScribble &&
        !isInRecipe &&
        css`
          ${stretch('left')}
          ${stretch('right')}
        `}
        ${isInRecipe &&
        css`
          ${stretchToFitCard('left')}
          ${stretchToFitCard('right')}
        `}
      `}
    `
  }}
`

const VideoContainer: FunctionComponent<VideoContainerProps> = ({
  videoId,
  isInOverlay,
  isInVideoOnlyArticle,
  hasPlaylist,
  children,
}) => {
  const isInArticleContent = useIsInArticleContent()
  const isInScribbleContent = useIsInScribbleContent()
  const isInRecipeContent = useIsInRecipeContent()
  const isInRecipeIngredients = useIsInRecipeIngredientsContent()

  return (
    <StyledVideoContainer
      // WARNING: Unless you want to have a heart attack please make sure that for different videoId the component is rerendered,
      // because the BrightcoveVideoWebWrapper never rerenders!!!
      key={videoId}
      videoId={videoId}
      isInArticleContent={isInArticleContent}
      isInScribble={isInScribbleContent}
      isInRecipe={isInRecipeContent}
      isInRecipeIngredients={isInRecipeIngredients}
      isInOverlay={isInOverlay}
      isInVideoOnlyArticle={isInVideoOnlyArticle}
      hasPlaylist={hasPlaylist}>
      {children}
    </StyledVideoContainer>
  )
}

export default VideoContainer
